/**
 * PlanEditor Component
 * Main component for editing existing waste management plans
 */

import { useState, useCallback } from 'react';
import type { Plan, PlanData, ProjectInfo, ApplicantInfo, WasteStream } from '../../types';
import { CollapsibleSection } from './CollapsibleSection';
import { WasteStreamsEditor } from './WasteStreamsEditor';
import { calculateCompliance } from '../../utils/calgreen';
import { validation } from '../../utils/validation';
import { formatPlanStatus, getStatusColor } from '../../utils/formatting';

export interface PlanEditorProps {
  plan: Plan;
  planData: PlanData;
  onSave: (updates: Partial<PlanData>) => Promise<void>;
  onCancel?: () => void;
}

export function PlanEditor({ plan, planData, onSave, onCancel }: PlanEditorProps) {
  // Local state for editing
  const [localPlanData, setLocalPlanData] = useState<PlanData>(planData);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Track if there are unsaved changes
  const hasChanges = JSON.stringify(localPlanData) !== JSON.stringify(planData);

  // Project info handlers
  const handleProjectInfoChange = useCallback((field: keyof ProjectInfo, value: string | number) => {
    setLocalPlanData((prev) => ({
      ...prev,
      project_info: {
        ...prev.project_info,
        [field]: value,
      },
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Applicant info handlers
  const handleApplicantInfoChange = useCallback((field: keyof ApplicantInfo, value: string) => {
    setLocalPlanData((prev) => ({
      ...prev,
      applicant_info: {
        ...prev.applicant_info,
        [field]: value,
      },
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Waste streams handler
  const handleWasteStreamsChange = useCallback((wasteStreams: WasteStream[]) => {
    // Recalculate compliance when waste streams change
    const compliance = calculateCompliance(wasteStreams);

    setLocalPlanData((prev) => ({
      ...prev,
      waste_streams: wasteStreams,
      compliance,
    }));
  }, []);

  // Notes handler
  const handleNotesChange = useCallback((notes: string) => {
    setLocalPlanData((prev) => ({
      ...prev,
      notes,
    }));
  }, []);

  // Validation
  const validatePlanData = useCallback((): boolean => {
    const errors = validation.validatePlanData(localPlanData);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [localPlanData]);

  // Save handler
  const handleSave = async () => {
    setError(null);

    // Validate before saving
    if (!validatePlanData()) {
      setError('Please fix the validation errors before saving');
      return;
    }

    setIsSaving(true);

    try {
      await onSave(localPlanData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
      console.error('Error saving plan:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Project type options
  const projectTypeOptions = [
    { value: 'single_family_new', label: 'Single-Family New Construction' },
    { value: 'multifamily_new', label: 'Multifamily New Construction' },
    { value: 'adu', label: 'Accessory Dwelling Unit (ADU)' },
    { value: 'residential_addition', label: 'Residential Addition' },
    { value: 'kitchen_bath_remodel', label: 'Kitchen/Bathroom Remodel' },
    { value: 'residential_renovation', label: 'Residential Renovation/Remodel' },
    { value: 'reroofing_residential', label: 'Reroofing - Residential' },
    { value: 'demolition_residential', label: 'Demolition - Residential' },
    { value: 'commercial_new', label: 'Commercial New Construction' },
    { value: 'tenant_improvement', label: 'Tenant Improvement (TI)' },
    { value: 'reroofing_commercial', label: 'Reroofing - Commercial' },
    { value: 'demolition_commercial', label: 'Demolition - Commercial' },
    { value: 'mixed', label: 'Mixed Use/Other' },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Sticky Header */}
      <div className="bg-white border-b sticky top-0 z-10 px-6 py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              {localPlanData.project_info.project_name}
            </h1>
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  plan.status
                )}`}
              >
                {formatPlanStatus(plan.status)}
              </span>
              {hasChanges && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600">
                  <span className="w-2 h-2 bg-orange-500 rounded-full" aria-hidden="true"></span>
                  Unsaved Changes
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary"
              disabled={!hasChanges || isSaving}
              aria-busy={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-4">
        {/* Project Information Section */}
        <CollapsibleSection
          title="Project Information"
          defaultOpen={true}
          hasChanges={
            JSON.stringify(localPlanData.project_info) !== JSON.stringify(planData.project_info)
          }
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="project-name" className="block text-sm font-medium text-neutral-700 mb-1">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                id="project-name"
                type="text"
                value={localPlanData.project_info.project_name}
                onChange={(e) => handleProjectInfoChange('project_name', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.project_name ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.project_name ? 'true' : 'false'}
                aria-describedby={validationErrors.project_name ? 'project-name-error' : undefined}
              />
              {validationErrors.project_name && (
                <p id="project-name-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.project_name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="project-address" className="block text-sm font-medium text-neutral-700 mb-1">
                Project Address <span className="text-red-500">*</span>
              </label>
              <input
                id="project-address"
                type="text"
                value={localPlanData.project_info.project_address}
                onChange={(e) => handleProjectInfoChange('project_address', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.project_address ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.project_address ? 'true' : 'false'}
                aria-describedby={validationErrors.project_address ? 'project-address-error' : undefined}
              />
              {validationErrors.project_address && (
                <p id="project-address-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.project_address}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="project-type" className="block text-sm font-medium text-neutral-700 mb-1">
                Project Type <span className="text-red-500">*</span>
              </label>
              <select
                id="project-type"
                value={localPlanData.project_info.project_type}
                onChange={(e) => handleProjectInfoChange('project_type', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.project_type ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.project_type ? 'true' : 'false'}
                aria-describedby={validationErrors.project_type ? 'project-type-error' : undefined}
              >
                <option value="">Select project type...</option>
                {projectTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {validationErrors.project_type && (
                <p id="project-type-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.project_type}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="start-date" className="block text-sm font-medium text-neutral-700 mb-1">
                  Estimated Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="start-date"
                  type="date"
                  value={localPlanData.project_info.estimated_start_date}
                  onChange={(e) => handleProjectInfoChange('estimated_start_date', e.target.value)}
                  className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                    validationErrors.estimated_start_date ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  required
                  aria-invalid={validationErrors.estimated_start_date ? 'true' : 'false'}
                  aria-describedby={validationErrors.estimated_start_date ? 'start-date-error' : undefined}
                />
                {validationErrors.estimated_start_date && (
                  <p id="start-date-error" className="text-sm text-red-600 mt-1" role="alert">
                    {validationErrors.estimated_start_date}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="completion-date" className="block text-sm font-medium text-neutral-700 mb-1">
                  Estimated Completion Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="completion-date"
                  type="date"
                  value={localPlanData.project_info.estimated_completion_date}
                  onChange={(e) => handleProjectInfoChange('estimated_completion_date', e.target.value)}
                  className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                    validationErrors.estimated_completion_date ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  required
                  aria-invalid={validationErrors.estimated_completion_date ? 'true' : 'false'}
                  aria-describedby={validationErrors.estimated_completion_date ? 'completion-date-error' : undefined}
                />
                {validationErrors.estimated_completion_date && (
                  <p id="completion-date-error" className="text-sm text-red-600 mt-1" role="alert">
                    {validationErrors.estimated_completion_date}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="square-footage" className="block text-sm font-medium text-neutral-700 mb-1">
                Total Square Footage
              </label>
              <input
                id="square-footage"
                type="number"
                value={localPlanData.project_info.total_square_footage || ''}
                onChange={(e) => handleProjectInfoChange('total_square_footage', parseInt(e.target.value) || 0)}
                className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
                min={0}
                inputMode="numeric"
              />
              <p className="text-sm text-neutral-500 mt-1">
                Optional: Enter the total square footage of the project
              </p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Applicant Information Section */}
        <CollapsibleSection
          title="Applicant Information"
          hasChanges={
            JSON.stringify(localPlanData.applicant_info) !== JSON.stringify(planData.applicant_info)
          }
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-neutral-700 mb-1">
                Contact Name <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-name"
                type="text"
                value={localPlanData.applicant_info.contact_name}
                onChange={(e) => handleApplicantInfoChange('contact_name', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.contact_name ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.contact_name ? 'true' : 'false'}
                aria-describedby={validationErrors.contact_name ? 'contact-name-error' : undefined}
              />
              {validationErrors.contact_name && (
                <p id="contact-name-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.contact_name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-neutral-700 mb-1">
                Contact Email <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-email"
                type="email"
                value={localPlanData.applicant_info.contact_email}
                onChange={(e) => handleApplicantInfoChange('contact_email', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.contact_email ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.contact_email ? 'true' : 'false'}
                aria-describedby={validationErrors.contact_email ? 'contact-email-error' : undefined}
              />
              {validationErrors.contact_email && (
                <p id="contact-email-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.contact_email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="contact-phone" className="block text-sm font-medium text-neutral-700 mb-1">
                Contact Phone <span className="text-red-500">*</span>
              </label>
              <input
                id="contact-phone"
                type="tel"
                value={localPlanData.applicant_info.contact_phone}
                onChange={(e) => handleApplicantInfoChange('contact_phone', e.target.value)}
                className={`input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg ${
                  validationErrors.contact_phone ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                required
                aria-invalid={validationErrors.contact_phone ? 'true' : 'false'}
                aria-describedby={validationErrors.contact_phone ? 'contact-phone-error' : undefined}
              />
              {validationErrors.contact_phone && (
                <p id="contact-phone-error" className="text-sm text-red-600 mt-1" role="alert">
                  {validationErrors.contact_phone}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="company-name" className="block text-sm font-medium text-neutral-700 mb-1">
                Company Name
              </label>
              <input
                id="company-name"
                type="text"
                value={localPlanData.applicant_info.company_name || ''}
                onChange={(e) => handleApplicantInfoChange('company_name', e.target.value)}
                className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
              />
              <p className="text-sm text-neutral-500 mt-1">
                Optional: Enter your company name
              </p>
            </div>

            <div>
              <label htmlFor="license-number" className="block text-sm font-medium text-neutral-700 mb-1">
                License Number
              </label>
              <input
                id="license-number"
                type="text"
                value={localPlanData.applicant_info.license_number || ''}
                onChange={(e) => handleApplicantInfoChange('license_number', e.target.value)}
                className="input-field w-full px-4 py-3 md:py-2 text-base border rounded-lg"
              />
              <p className="text-sm text-neutral-500 mt-1">
                Optional: Enter your contractor license number
              </p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Waste Streams Section */}
        <CollapsibleSection
          title="Waste Streams"
          badge={localPlanData.waste_streams.length}
          hasChanges={
            JSON.stringify(localPlanData.waste_streams) !== JSON.stringify(planData.waste_streams)
          }
        >
          <WasteStreamsEditor
            wasteStreams={localPlanData.waste_streams}
            onChange={handleWasteStreamsChange}
            compliance={localPlanData.compliance}
          />
        </CollapsibleSection>

        {/* Compliance Section - Read Only */}
        <CollapsibleSection title="Compliance Calculations" defaultOpen={false}>
          <div className="space-y-4">
            <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Total Generated</div>
                  <div className="text-xl font-semibold text-neutral-900">
                    {localPlanData.compliance.total_waste_generated.toFixed(2)} tons
                  </div>
                </div>
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Total Diverted</div>
                  <div className="text-xl font-semibold text-neutral-900">
                    {localPlanData.compliance.total_waste_diverted.toFixed(2)} tons
                  </div>
                </div>
                <div>
                  <div className="text-sm text-neutral-600 mb-1">Diversion Rate</div>
                  <div
                    className={`text-xl font-semibold ${
                      localPlanData.compliance.meets_calgreen_requirement
                        ? 'text-accent-600'
                        : 'text-red-600'
                    }`}
                  >
                    {localPlanData.compliance.diversion_rate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-neutral-600 mt-1">
                    {localPlanData.compliance.meets_calgreen_requirement
                      ? 'Meets CalGreen 5.408'
                      : 'Does Not Meet CalGreen 5.408'}
                  </div>
                </div>
              </div>
            </div>

            {!localPlanData.compliance.meets_calgreen_requirement && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-1">
                      CalGreen Requirement Not Met
                    </h4>
                    <p className="text-sm text-yellow-700">
                      CalGreen 5.408 requires a minimum diversion rate of 65%. Consider adding more
                      recyclable or reusable waste streams to meet compliance.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>

        {/* Notes Section */}
        <CollapsibleSection
          title="Additional Notes"
          hasChanges={(localPlanData.notes || '') !== (planData.notes || '')}
        >
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-neutral-700 mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              value={localPlanData.notes || ''}
              onChange={(e) => handleNotesChange(e.target.value)}
              rows={5}
              className="input-field w-full px-4 py-3 text-base border rounded-lg"
              placeholder="Add any additional notes or comments about this plan..."
            />
            <p className="text-sm text-neutral-500 mt-1">
              Optional: Add any additional information, special requirements, or comments
            </p>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
